import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RiCalendar2Line } from "react-icons/ri";
import Select from "react-select";
import currencies from "../../Data/Currency.json";
import ImageViewModal from "../Package/ImageViewModal";
import PackageDateModal from "../Package/PackageDateModal"; // Kept if you still use package dates
import '../Package/PackageForm.css';
import packageDestinationsData from '../../Data/PackageDestinations.json';

const DEFAULT_IMAGE = "/assets/images/default-package.jpg";

// Updated Initial Values to match Holiday Model
const initialFieldValues = {
  holidayId: "00000000-0000-0000-0000-000000000000",
  holidayName: "",
  description: "",
  travelType: "FAMILY",
  imageName: "",
  imageFile: null,
  imageSrc: DEFAULT_IMAGE,
  noOfPersons: "1",
  adults: "0",
  kids: "0",
  infants: "0",
  destinationsCover: "",
  duration: "3 Nights / 4 Days",
  startedDate: moment().format("DD-MMM-YYYY"),
  status: "Active",
  holidayType: "Holiday",
  price: "",
  currency: "",
  contactName: "",
  contactPhoneNumber: "",
  contactEmail: "",
  includes: "flight, hotel, food", // Kept for UI compatibility mapping
  packageDateId: "" // Kept if used
};

const CustomDateInput = React.forwardRef(({ value, onClick, errorClass }, ref) => (
  <div className="input-group">
    <input
      type="text"
      className={`form-control ${errorClass}`}
      value={value}
      onClick={onClick}
      ref={ref}
      readOnly
    />
    <div className="input-group-text bg-primary border-primary text-white" style={{ cursor: "pointer" }} onClick={onClick}>
      <RiCalendar2Line />
    </div>
  </div>
));

const HolidayForm = ({
  packageId, // This acts as holidayId
  isEditMode,
  setIsEditMode,
  onPackageCreated,
  packageDetails,
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [btnLoading, setBtnLoading] = useState(false);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [showImageViewModal, setShowImageViewModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(DEFAULT_IMAGE);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [packageDates, setPackageDates] = useState([]);
  const [showPackageDateModal, setShowPackageDateModal] = useState(false);

  // --- DESTINATION AUTOCOMPLETE LOGIC ---
  const allDestinations = useMemo(() => {
    const destinationList = [];
    if (packageDestinationsData) {
      packageDestinationsData.forEach(countryData => {
        if (countryData.Destination) {
          destinationList.push(countryData.Destination);
          if (countryData.Places && Array.isArray(countryData.Places)) {
            countryData.Places.forEach(place => {
              destinationList.push(`${place}-${countryData.Destination}`);
            });
          }
        }
      });
    }
    return destinationList;
  }, []);

  const handleDestinationChange = (e) => {
    const userInput = e.target.value;
    // Map to destinationsCover 
    setValues({ ...values, destinationsCover: userInput });

    if (userInput) {
      const filteredSuggestions = allDestinations.filter(
        suggestion => suggestion.toLowerCase().indexOf(userInput.toLowerCase()) > -1
      );
      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const mainDestinationName = suggestion.includes('-') ? suggestion.split('-').pop() : suggestion;

    // Auto-fill description based on destination if available
    const destinationData = packageDestinationsData.find(
      item => item.Destination.toLowerCase() === mainDestinationName.toLowerCase()
    );

    setValues(prevValues => ({
      ...prevValues,
      destinationsCover: suggestion,
      description: destinationData ? destinationData.Description : prevValues.description,
    }));

    setSuggestions([]);
    setShowSuggestions(false);
  };

  // --- LOAD DATA ON EDIT ---
  useEffect(() => {
    if (packageDetails) {
      // Map Holiday API response to Form Values
      setValues({
        ...initialFieldValues,
        holidayId: packageDetails.holidayId,
        holidayName: packageDetails.holidayName || "",
        description: packageDetails.description || "",
        travelType: packageDetails.travelType || "FAMILY",
        destinationsCover: packageDetails.destinationsCover || "",
        duration: packageDetails.duration || "",
        startedDate: packageDetails.startedDate ? moment(packageDetails.startedDate).format("DD-MMM-YYYY") : moment().format("DD-MMM-YYYY"),
        noOfPersons: packageDetails.noOfPersons?.toString() || "1",
        price: packageDetails.salePrice || packageDetails.price || "", // Map price
        currency: packageDetails.currency || "INR",
        status: packageDetails.status || "Active",
        contactName: packageDetails.contactName || "",
        contactEmail: packageDetails.contactEmail || "",
        contactPhoneNumber: packageDetails.contactPhoneNumber || "",
        imageSrc: packageDetails.imageSrc || DEFAULT_IMAGE,
        // includes: ... (map if you have a way to extract it from HolidayInclusions json string)
      });
      setImagePreview(packageDetails.imageSrc || DEFAULT_IMAGE);
      setIsEditMode(true);
    } else {
      // CREATE MODE
      const newValues = { ...initialFieldValues };
      if (user) {
        newValues.contactName = user.name || "";
        newValues.contactPhoneNumber = user.phoneNumber || "";
        newValues.contactEmail = user.email || "";
        newValues.currency = user.currency || "INR";
      }
      setValues(newValues);
      setImagePreview(DEFAULT_IMAGE);
      setIsEditMode(false);
      setErrors({});
    }
  }, [packageDetails, user, setIsEditMode]);

  const GetPackageDates = async (packageType) => {
    try {
      const url = `${config.GETDATESBYTYPE}/${user.cid}?type=${packageType}`;
      const res = await apiService.getAll(url);
      setPackageDates(res);
    } catch (error) {
      console.error("Error fetching package dates:", error);
      setPackageDates([]);
    }
  };

  useEffect(() => {
    if (user?.cid) GetPackageDates("Holiday");
  }, [user]);

  const handlePackageDateCreated = () => GetPackageDates("Holiday");

  const validate = () => {
    let temp = {};
    temp.holidayName = values.holidayName !== "";
    temp.duration = values.duration !== "";
    temp.startedDate = values.startedDate !== "";
    temp.noOfPersons = values.noOfPersons !== "" && values.noOfPersons > 0;
    temp.price = values.price !== "" && !isNaN(values.price);
    temp.currency = values.currency !== "";
    temp.description = values.description !== "";
    // Image validation: required on create, optional on edit
    temp.imageFile = isEditMode ? true : values.imageFile !== null;
    temp.status = values.status !== "";

    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleFileSelected = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageSrc = reader.result;
        setImagePreview(imageSrc);
        setValues({
          ...values,
          imageFile: file,
          imageSrc: imageSrc,
          imageName: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      const formData = new FormData();

      // Append basic fields
      Object.entries({
        ...values,
        companyId: user?.cid,
        userId: user?.id,
        createdBy: user?.companyType,
        holidayName: values.holidayName,
        salePrice: values.price, // Map 'price' input to 'SalePrice'
      }).forEach(([key, value]) => formData.append(key, value ?? ""));

      if (values.imageFile) {
        formData.append("imageFile", values.imageFile);
      }

      try {
        if (isEditMode) {
          // UPDATE
          // Ensure you have this endpoint in APIConfig
          await apiService.update(config.UPDATEHOLIDAY, formData);

        } else {
          // CREATE (Manual)
          // Ensure you have this endpoint in APIConfig
          const response = await apiService.create(config.CREATE_HOLIDAY, formData);

          if (response && response.holidayId) {
            toast.success("Holiday created successfully!");
            onPackageCreated(response.holidayId);
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        // apiService usually handles error toasts
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handlePersonChange = (amount) => {
    setValues((prev) => {
      const currentPersons = parseInt(prev.noOfPersons, 10) || 0;
      const newPersons = Math.max(1, currentPersons + amount);
      return { ...prev, noOfPersons: newPersons.toString() };
    });
  };

  const resetForm = () => {
    const contactInfo = {
      contactName: values.contactName,
      contactPhoneNumber: values.contactPhoneNumber,
      contactEmail: values.contactEmail,
      currency: values.currency,
    };
    setValues({ ...initialFieldValues, ...contactInfo });
    setImagePreview(DEFAULT_IMAGE);
    setBtnLoading(false);
    setErrors({});
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " form-control-danger" : "";

  return (
    <div className="PackageForm-wrapper">
      <PackageDateModal show={showPackageDateModal} onHide={() => setShowPackageDateModal(false)} onPackageDateCreated={handlePackageDateCreated} type="Holiday" />
      <ImageViewModal show={showImageViewModal} onHide={() => setShowImageViewModal(false)} imageSrc={imagePreview} title="Package Image" defaultImage={DEFAULT_IMAGE} aspectRatio="4/3" />

      <div className="PackageForm-header d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">{isEditMode ? "Edit PACKAGE" : "Create New PACKAGE"}</h5>
        {/* No AI Button Here anymore */}
      </div>

      <form onSubmit={handleSubmit} autoComplete="off" noValidate>
        <div className="card PackageForm-section-card">
          <div className="card-body">
            <div className="row">
              <div className="col-lg-8">
                <div className="row">

                  <div className="col-md-6 mb-3 Destinationautocomplete-container">
                    <label className="PackageForm-label form-label d-flex align-items-center">
                      <i className="ri-map-pin-line me-2"></i>Destination
                    </label>
                    <input
                      type="text"
                      name="destinationsCover" // Mapped to destinationsCover
                      value={values.destinationsCover}
                      onChange={handleDestinationChange}
                      className={"form-control" + applyErrorClass("destinationsCover")}
                      placeholder="Dubai or Paris"
                      autoComplete="off"
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    />
                    {showSuggestions && suggestions.length > 0 && (
                      <ul className="Destinationautocomplete-suggestions-list">
                        {suggestions.slice(0, 10).map((suggestion, index) => (
                          <li
                            key={index}
                            className="Destinationautocomplete-suggestion-item"
                            onMouseDown={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    )}
                    {errors.destinationsCover === false && (
                      <div className="validationerror">Please enter a destination</div>
                    )}
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label d-flex align-items-center">
                      <i className="ri-road-map-line me-2"></i>Destinations Cover
                    </label>
                    <input
                      type="text"
                      name="destinationsCover"
                      value={values.destinationsCover}
                      onChange={handleInputChange}
                      className={
                        "form-control " + applyErrorClass("destinationsCover")
                      }
                      placeholder="Dubai, Abu Dhabi, Sharjah"
                    />
                    {errors.destinationsCover === false && (
                      <div className="validationerror">
                        Please enter destinations covered
                      </div>
                    )}
                  </div>
                  {/* Package Name */}
                  <div className="col-12 mb-3">
                    <label className="PackageForm-label form-label d-flex align-items-center">
                      <i className="ri-medal-line me-2"></i>Package Name
                    </label>
                    <input
                      type="text"
                      name="holidayName" // Changed to holidayName
                      value={values.holidayName}
                      onChange={handleInputChange}
                      className={"form-control form-control-md" + applyErrorClass("holidayName")}
                      placeholder="e.g. A 5-Day Luxury Escape to Dubai"
                    />
                    {errors.holidayName === false && (
                      <div className="validationerror">Please enter package name</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="PackageForm-label form-label d-flex align-items-center">
                      <i className="ri-suitcase-line me-2"></i>Travel Type
                    </label>
                    <select
                      name="travelType"
                      value={values.travelType}
                      onChange={handleInputChange}
                      className={"form-select" + applyErrorClass("travelType")}
                    >
                      <option value="">Select Travel Type</option>
                      <option value="FAMILY">FAMILY</option>
                      <option value="CORPORATE">CORPORATE</option>
                      <option value="GROUP">GROUP</option>
                      <option value="HONEYMOON">HONEYMOON</option>
                    </select>
                    {errors.travelType === false && (
                      <div className="validationerror">Please select a travel type</div>
                    )}
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label d-flex align-items-center">
                      <i className="ri-check-double-line me-2"></i>Includes
                    </label>
                    <Select
                      isMulti
                      options={[
                        { value: "food", label: "Food" },
                        { value: "hotel", label: "Hotel" },
                        { value: "flight", label: "Flight" },
                      ]}
                      value={
                        values.includes
                          ? values.includes
                            .split(", ")
                            .map((item) => ({
                              value: item,
                              label:
                                item.charAt(0).toUpperCase() + item.slice(1),
                            }))
                          : []
                      }
                      onChange={(opts) =>
                        setValues({
                          ...values,
                          includes: opts.map((o) => o.value).join(", "),
                        })
                      }
                      className={
                        "react-select-container" +
                        (errors.includes === false ? " is-invalid" : "")
                      }
                      classNamePrefix="react-select"
                    />
                    {errors.includes === false && (
                      <div className="invalid-feedback d-block">
                        Please select at least one option
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div className="col-lg-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelected}
                  style={{ display: 'none' }}
                  accept="image/png, image/jpeg, image/gif"
                />
                <div className={`PackageForm-image-upload-box text-center p-1 border rounded ${errors.imageFile === false && !isEditMode ? 'border-danger' : ''}`}>
                  <img
                    src={imagePreview}
                    alt="Package Preview"
                    className="img-fluid rounded mb-3"
                    style={{ maxHeight: "150px" }}
                  />
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <i className="ri-upload-2-line me-1"></i> Upload
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowImageViewModal(true)}
                    >
                      <i className="ri-eye-line me-1"></i> View
                    </button>
                  </div>
                  {errors.imageFile === false && !isEditMode && (
                    <div className="validationerror mt-2">Please upload an image</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="PackageForm-label form-label d-flex align-items-center">
                  <i className="ri-calendar-event-line me-2"></i>Travel Date
                </label>
                <DatePicker
                  selected={values.startedDate ? moment(values.startedDate, "DD-MMM-YYYY").toDate() : null}
                  onChange={(date) => {
                    handleInputChange({
                      target: {
                        name: "startedDate",
                        value: moment(date).format("DD-MMM-YYYY"),
                      },
                    });
                  }}
                  customInput={<CustomDateInput errorClass={applyErrorClass("startedDate")} />}
                  dateFormat="dd-MMM-yyyy"
                  showYearDropdown
                  minDate={new Date()}
                />
                {errors.startedDate === false && <div className="validationerror">Please select</div>}
              </div>
              <div className="col-md-3 mb-3">
                <label className="PackageForm-label form-label d-flex align-items-center"><i className="ri-time-line me-2"></i>Duration</label>
                <select name="duration" value={values.duration} onChange={handleInputChange} className={"form-select " + applyErrorClass("duration")}>
                  <option value="">Select Duration</option>
                  {Array.from({ length: 30 }, (_, i) => {
                    const nights = i + 1; const days = nights + 1;
                    return <option key={nights} value={`${nights} Nights / ${days} Days`}>{`${nights} Night${nights > 1 ? "s" : ""} / ${days} Day${days > 1 ? "s" : ""}`}</option>;
                  })}
                </select>
                {errors.duration === false && <div className="validationerror">Please select</div>}
              </div>
              <div className="col-md-2 mb-3">
                <label className="PackageForm-label form-label d-flex align-items-center"><i className="ri-group-line me-2"></i>Persons</label>
                <div className="input-group">
                  <button className="btn btn-outline-secondary" type="button" onClick={() => handlePersonChange(-1)}>-</button>
                  <input type="number" name="noOfPersons" value={values.noOfPersons} onChange={handleInputChange} className={"form-control text-center" + applyErrorClass("noOfPersons")} min="1" />
                  <button className="btn btn-outline-secondary" type="button" onClick={() => handlePersonChange(1)}>+</button>
                </div>
                {errors.noOfPersons === false && <div className="validationerror">Enter a valid number</div>}
              </div>
              <div className="col-md-3 mb-3">
                <label className="PackageForm-label form-label d-flex align-items-center"><i className="ri-money-dollar-circle-line me-2"></i>Price</label>
                <div className="input-group">
                  <select name="currency" value={values.currency} onChange={handleInputChange} className={"input-group-text" + applyErrorClass("currency")}>
                    {currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </select>
                  <input type="number" name="price" value={values.price} onChange={handleInputChange} className={"form-control " + applyErrorClass("price")} placeholder="2000" min="1" />
                </div>
                {errors.price === false && <div className="validationerror">Enter valid price</div>}
              </div>
              <div className="col-12 mb-3">
                <label className="PackageForm-label form-label d-flex align-items-center"><i className="ri-file-text-line me-2"></i>Description</label>
                <textarea name="description" value={values.description} onChange={handleInputChange} className={"form-control " + applyErrorClass("description")} placeholder="Give a captivating description..." rows="4"></textarea>
                {errors.description === false && <div className="validationerror">Please enter description</div>}
              </div>
            </div>

            <div className="col-12 text-center my-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? "Hide" : "Show"} Advanced Options <i className={`ri-arrow-${showAdvanced ? "up" : "down"}-s-line`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* --- ADVANCED SECTION --- */}
        {showAdvanced && (
          <div className="card form-section-card" style={{ backgroundColor: "#c1d56aff" }}>
            <div className="card-body">
              <h5 className="section-title">Advanced Details</h5>
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <label className="form-label d-flex align-items-center">
                    <i className="ri-calendar-2-line me-2"></i>Package Dates
                  </label>
                  <div className="input-group">
                    <select
                      name="packageDateId"
                      value={values.packageDateId}
                      onChange={handleInputChange}
                      className={
                        "form-select" + applyErrorClass("packageDateId")
                      }
                    >
                      <option value="">Please select a date range</option>
                      {packageDates.map((pd) => (
                        <option
                          key={pd.packageDateId}
                          value={pd.packageDateId}
                        >{`${moment(pd.startDate).format(
                          "DD MMM YYYY"
                        )} - ${moment(pd.endDate).format(
                          "DD MMM YYYY"
                        )}`}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPackageDateModal(true)}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="col-lg-6 mb-3">
                  <label className="form-label"><i className="ri-toggle-line me-2"></i>Status</label>
                  <select name="status" value={values.status} onChange={handleInputChange} className={"form-select " + applyErrorClass("status")}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card form-section-card" style={{ backgroundColor: "#6aa0d5ff" }}>
          <div className="card-body">
            <h5 className="section-title">Contact Information</h5>
            <div className="row">
              <div className="col-lg-4 mb-3">
                <label className="form-label"><i className="ri-user-line me-2"></i>Name</label>
                <input type="text" name="contactName" value={values.contactName} onChange={handleInputChange} className={"form-control " + applyErrorClass("contactName")} />
              </div>
              <div className="col-lg-4 mb-3">
                <label className="form-label"><i className="ri-phone-line me-2"></i>Phone</label>
                <input type="tel" maxLength={15} name="contactPhoneNumber" value={values.contactPhoneNumber} onChange={handleInputChange} className={"form-control " + applyErrorClass("contactPhoneNumber")} />
              </div>
              <div className="col-lg-4 mb-3">
                <label className="form-label"><i className="ri-mail-line me-2"></i>Email</label>
                <input type="text" maxLength={40} name="contactEmail" value={values.contactEmail} onChange={handleInputChange} className={"form-control " + applyErrorClass("contactEmail")} />
              </div>
            </div>
          </div>
        </div>

        <div className="PackageForm-actions-footer">
          <div className="hstack gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={resetForm}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={btnLoading}>
              {btnLoading ? "Saving..." : isEditMode ? "Update Holiday" : "Create Holiday"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default HolidayForm;